import { usePermission } from '@/lib/permissions';
import { useReorderCandidates, useVacancyPipeline } from '@/hooks/use-recruitment';
import type { ReorderCandidateItem } from '@/lib/recruitment';
import type {
  PipelineCard as PipelineCardData,
  PipelineStageBucket,
  VacancyDetail,
} from '@/types/recruitment';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  defaultDropAnimationSideEffects,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type DropAnimation,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { recruitmentKeys } from '@/hooks/use-recruitment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { consentRevokedMessage } from '../lib/consent-actions';
import { AddExistingCandidatesDialog } from './add-existing-candidates-dialog';
import { CandidateProfileModal } from './candidate-profile-modal';
import { CreateCandidateDialog } from './create-candidate-dialog';
import { HireCandidateDialog } from './hire-candidate-dialog';
import { MoveToOfferConfirmDialog } from './move-to-offer-confirm-dialog';
import { PipelineCard } from './pipeline-card';
import { PipelineColumn } from './pipeline-column';
import { PipelineControlBar } from './pipeline-control-bar';
import { RejectReasonDialog } from './reject-reason-dialog';
import { VacancySettingsDialog } from './vacancy-settings-dialog';
import { Route as VacancyRoute } from '@/routes/recruitment.vacancies_.$id';

interface VacancyCycleTabProps {
  vacancy: VacancyDetail;
}

const TERMINAL_VACANCY_STATUSES = new Set(['ARCHIVED', 'CANCELLED', 'CLOSED']);

const dropAnimation: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.3' } },
  }),
};

export function VacancyCycleTab({ vacancy }: VacancyCycleTabProps) {
  const routeNavigate = useNavigate({ from: VacancyRoute.fullPath });
  const routeSearch = VacancyRoute.useSearch();

  const canMove = usePermission('recruitment.candidates.move');
  const canCreateCandidate = usePermission('recruitment.candidates.create');
  const canAssignCandidate = usePermission('recruitment.candidates.edit');
  const isVacancyTerminal = TERMINAL_VACANCY_STATUSES.has(vacancy.status);
  const vacancyAcceptsCandidates =
    vacancy.status === 'DRAFT' ||
    vacancy.status === 'UNDER_REVIEW' ||
    vacancy.status === 'ACTIVE';

  const [searchInput, setSearchInput] = useState(routeSearch.q ?? '');
  const [debouncedSearch, setDebouncedSearch] = useState(routeSearch.q ?? '');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    routeNavigate({
      search: (prev) => ({ ...prev, q: debouncedSearch || undefined }),
      replace: true,
    });
  }, [debouncedSearch, routeNavigate]);

  const { data, isLoading, error } = useVacancyPipeline(vacancy.id);
  const reorderMutation = useReorderCandidates(vacancy.id);
  const queryClient = useQueryClient();

  const commitReorder = useCallback(
    (items: ReorderCandidateItem[], errorMsg: string) => {
      reorderMutation.mutate(items, {
        onError: (err) => {
          // HW-1055: a candidate who revoked consent cannot be moved on — say so
          // instead of the generic failure.
          toast.error(consentRevokedMessage(err) ?? errorMsg);
          setLocalStages(null);
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: recruitmentKeys.vacancyDetail(vacancy.id),
          });
          setLocalStages(null);
        },
      });
    },
    [reorderMutation, queryClient, vacancy.id],
  );

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [addExistingOpen, setAddExistingOpen] = useState(false);

  // --- Modal state: track the opened candidateVacancyId and re-derive card+stage from live data.
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  const handleOpenCard = useCallback((card: PipelineCardData) => {
    setOpenCardId(card.candidateVacancyId);
  }, []);

  // --- Scroll preservation (horizontal, debounced 500ms)
  const scrollRef = useRef<HTMLDivElement>(null);
  const initialScrollRef = useRef<number | undefined>(routeSearch.scroll);

  useEffect(() => {
    if (scrollRef.current && initialScrollRef.current != null) {
      scrollRef.current.scrollLeft = initialScrollRef.current;
      initialScrollRef.current = undefined;
    }
  }, [data]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const scrollLeft = e.currentTarget.scrollLeft;
      if (scrollSaveTimer.current) clearTimeout(scrollSaveTimer.current);
      scrollSaveTimer.current = setTimeout(() => {
        routeNavigate({
          search: (prev) => ({ ...prev, scroll: scrollLeft > 0 ? scrollLeft : undefined }),
          replace: true,
        });
      }, 500);
    },
    [routeNavigate],
  );
  const scrollSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Local DnD state (mirrors task-manager pattern)
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [localStages, setLocalStages] = useState<PipelineStageBucket[] | null>(null);

  const baseStages = data?.stages ?? null;

  const filteredStages = useMemo<PipelineStageBucket[] | null>(() => {
    if (!baseStages) return null;
    const query = debouncedSearch.toLowerCase().trim();
    if (!query) return baseStages;
    return baseStages.map((stage) => ({
      ...stage,
      cards: stage.cards.filter((c) => {
        const haystacks = [
          `${c.candidate.firstName} ${c.candidate.lastName}`,
          c.candidate.email ?? '',
          c.candidate.phone ?? '',
        ];
        return haystacks.some((h) => h.toLowerCase().includes(query));
      }),
    }));
  }, [baseStages, debouncedSearch]);

  const displayStages = localStages ?? filteredStages;

  // --- Card lookup maps
  const cardById = useMemo(() => {
    const map = new Map<string, PipelineCardData>();
    for (const s of baseStages ?? []) {
      for (const c of s.cards) map.set(c.candidateVacancyId, c);
    }
    return map;
  }, [baseStages]);

  const findStageId = useCallback(
    (id: UniqueIdentifier, stages: PipelineStageBucket[] | null): string | null => {
      if (!stages) return null;
      if (stages.some((s) => s.id === id)) return id as string;
      for (const s of stages) {
        if (s.cards.some((c) => c.candidateVacancyId === id)) return s.id;
      }
      return null;
    },
    [],
  );

  const activeCard = activeId ? cardById.get(activeId as string) ?? null : null;

  // --- DnD handlers
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const dragDisabled = !canMove || isVacancyTerminal;

  const handleDragStart = useCallback(
    (e: DragStartEvent) => {
      if (!baseStages) return;
      setActiveId(e.active.id);
      setLocalStages(baseStages.map((s) => ({ ...s, cards: [...s.cards] })));
    },
    [baseStages],
  );

  const handleDragOver = useCallback(
    (e: DragOverEvent) => {
      const { active, over } = e;
      if (!over || !localStages) return;
      const activeStageId = findStageId(active.id, localStages);
      const overStageId = findStageId(over.id, localStages);
      if (!activeStageId || !overStageId || activeStageId === overStageId) return;

      setLocalStages((prev) => {
        if (!prev) return prev;
        const next = prev.map((s) => ({ ...s, cards: [...s.cards] }));
        const fromIdx = next.findIndex((s) => s.id === activeStageId);
        const toIdx = next.findIndex((s) => s.id === overStageId);
        if (fromIdx === -1 || toIdx === -1) return prev;

        const cardIdx = next[fromIdx].cards.findIndex(
          (c) => c.candidateVacancyId === active.id,
        );
        if (cardIdx === -1) return prev;
        const [moved] = next[fromIdx].cards.splice(cardIdx, 1);

        const overCardIdx = next[toIdx].cards.findIndex(
          (c) => c.candidateVacancyId === over.id,
        );
        const insertIdx = overCardIdx >= 0 ? overCardIdx : next[toIdx].cards.length;
        next[toIdx].cards.splice(insertIdx, 0, moved);
        return next;
      });
    },
    [localStages, findStageId],
  );

  // HW-459: dropping into an Offer-typed stage requires a confirmation gate
  // (and the BE atomically flips Candidate.status to HIRING). We defer commitReorder
  // behind this state until the user confirms; cancel reverts the optimistic layout.
  const [pendingOfferMove, setPendingOfferMove] = useState<{
    items: ReorderCandidateItem[];
    errorMsg: string;
  } | null>(null);

  // HW-480: dropping into a HIRED-mapped stage opens the AddEmployeeDialog. The
  // reorder is held until the dialog resolves: success commits the convert call
  // (which the backend turns into the same stage move atomically); cancel reverts
  // the optimistic layout without hitting the server.
  const [pendingHireMove, setPendingHireMove] = useState<{
    card: PipelineCardData;
  } | null>(null);

  // HW-494: dropping into an ARCHIVED-mapped stage rejects the candidate. The
  // "Select Rejection Reason" modal collects a structured reason; the reorder is held
  // until the user confirms (reason attached) or cancels (optimistic layout reverts).
  const [pendingRejectMove, setPendingRejectMove] = useState<{
    items: ReorderCandidateItem[];
    errorMsg: string;
    card: PipelineCardData;
  } | null>(null);

  // Shared cross-column stage-move gating, used by both drag-and-drop and the
  // in-modal stage selector (HW-627). Decides whether the move commits straight
  // away or first opens a confirmation/dialog (HIRED lock, hire dialog, rejection
  // reason, OFFER confirmation).
  const applyStageMove = useCallback(
    (params: {
      card: PipelineCardData;
      items: ReorderCandidateItem[];
      errorMsg: string;
      targetStage: PipelineStageBucket;
      sourceStage: PipelineStageBucket | undefined;
    }) => {
      const { card, items, errorMsg, targetStage, sourceStage } = params;

      // HW-480: hired-locked card can only land in a HIRED-mapped stage. Mirror
      // the backend guard with a toast instead of a silent no-op.
      if (card.result === 'HIRED' && targetStage.candidateStatus !== 'HIRED') {
        toast.error('Hired candidates can only move into a Hired stage');
        setLocalStages(null);
        return;
      }

      // HW-480: moving a non-hired candidate into a HIRED-mapped column opens
      // AddEmployeeDialog; the convert endpoint performs the stage move server-side.
      if (targetStage.candidateStatus === 'HIRED' && card.result !== 'HIRED') {
        setPendingHireMove({ card });
        return;
      }

      // HW-494: moving into an ARCHIVED-mapped stage rejects the candidate — collect
      // a structured rejection reason before committing.
      if (targetStage.candidateStatus === 'ARCHIVED') {
        setPendingRejectMove({ items, errorMsg, card });
        return;
      }

      // HW-459: entering an OFFER-typed stage from a non-OFFER stage needs a
      // confirmation gate (the BE flips Candidate.status to HIRING).
      if (targetStage.type === 'OFFER' && sourceStage && sourceStage.type !== 'OFFER') {
        setPendingOfferMove({ items, errorMsg });
        return;
      }

      commitReorder(items, errorMsg);
    },
    [commitReorder],
  );

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      setActiveId(null);

      if (!over || !localStages || !baseStages) {
        setLocalStages(null);
        return;
      }

      const activeStageId = findStageId(active.id, localStages);
      const originalStageId = findStageId(active.id, baseStages);
      if (!activeStageId || !originalStageId) {
        setLocalStages(null);
        return;
      }

      const stage = localStages.find((s) => s.id === activeStageId);
      if (!stage) {
        setLocalStages(null);
        return;
      }
      const cardIds = stage.cards.map((c) => c.candidateVacancyId);
      const oldIdx = cardIds.indexOf(active.id as string);
      const overIdx = cardIds.indexOf(over.id as string);

      // If hovering over a different card, arrayMove to that index. If over.id
      // is a column body (overIdx === -1), keep the dragOver-established layout.
      let finalIds = cardIds;
      if (overIdx >= 0 && oldIdx !== overIdx) {
        finalIds = arrayMove(cardIds, oldIdx, overIdx);
        const byId = new Map(stage.cards.map((c) => [c.candidateVacancyId, c]));
        const reorderedCards = finalIds.map((id) => byId.get(id)!);
        setLocalStages((prev) =>
          prev
            ? prev.map((s) => (s.id === activeStageId ? { ...s, cards: reorderedCards } : s))
            : prev,
        );
      }

      const crossColumn = originalStageId !== activeStageId;
      if (!crossColumn && finalIds === cardIds && oldIdx === overIdx) {
        // Same column AND no position change — nothing to commit.
        setLocalStages(null);
        return;
      }

      const items: ReorderCandidateItem[] = finalIds.map((id, idx) => ({
        id,
        sortOrder: idx * 1000,
        ...(id === (active.id as string) && crossColumn ? { stageId: activeStageId } : {}),
      }));
      const errorMsg = crossColumn ? 'Failed to move candidate' : 'Failed to reorder candidate';

      // Pure reorder within a column never gates — commit straight away.
      const draggedCard = cardById.get(active.id as string);
      if (!crossColumn || !draggedCard) {
        commitReorder(items, errorMsg);
        return;
      }

      // Cross-column move: route through the shared stage-move gates (HIRED lock,
      // hire dialog, rejection reason, OFFER confirmation) — the same path the
      // in-modal stage selector uses (HW-627). The optimistic layout is held until
      // any opened dialog resolves; commitReorder clears it on settle.
      applyStageMove({
        card: draggedCard,
        items,
        errorMsg,
        targetStage: stage,
        sourceStage: baseStages.find((s) => s.id === originalStageId),
      });
    },
    [localStages, baseStages, findStageId, commitReorder, cardById, applyStageMove],
  );

  // --- Modal context derived from baseStages (not filteredStages: searching shouldn't
  // close the modal). Auto-closes when the card is no longer on the board.
  const modalContext = useMemo(() => {
    if (!openCardId || !baseStages) return null;
    for (const s of baseStages) {
      const idx = s.cards.findIndex((c) => c.candidateVacancyId === openCardId);
      if (idx >= 0) {
        return {
          card: s.cards[idx],
          stage: s,
          index: idx,
          total: s.cards.length,
          prevId: idx > 0 ? s.cards[idx - 1].candidateVacancyId : null,
          nextId: idx < s.cards.length - 1 ? s.cards[idx + 1].candidateVacancyId : null,
        };
      }
    }
    return null;
  }, [openCardId, baseStages]);

  useEffect(() => {
    if (openCardId && baseStages && !modalContext) {
      setOpenCardId(null);
    }
  }, [openCardId, baseStages, modalContext]);

  // HW-627: move the opened candidate to another stage via the in-modal selector.
  // The card is appended to the end of the target stage; openCardId is unchanged,
  // so once the pipeline refetches the modal re-derives the stage label, position
  // counter and prev/next context from baseStages automatically.
  const handleMoveStageFromModal = useCallback(
    (targetStageId: string) => {
      if (!baseStages || !modalContext) return;
      const { card, stage: sourceStage } = modalContext;
      if (targetStageId === sourceStage.id) return;
      const targetStage = baseStages.find((s) => s.id === targetStageId);
      if (!targetStage) return;

      const finalIds = [
        ...targetStage.cards.map((c) => c.candidateVacancyId),
        card.candidateVacancyId,
      ];
      const items: ReorderCandidateItem[] = finalIds.map((id, idx) => ({
        id,
        sortOrder: idx * 1000,
        ...(id === card.candidateVacancyId ? { stageId: targetStageId } : {}),
      }));

      applyStageMove({
        card,
        items,
        errorMsg: 'Failed to move candidate',
        targetStage,
        sourceStage,
      });
    },
    [baseStages, modalContext, applyStageMove],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PipelineControlBar
        search={searchInput}
        onSearchChange={setSearchInput}
        onOpenSettings={() => setSettingsOpen(true)}
        onAddCandidate={
          canCreateCandidate && vacancyAcceptsCandidates
            ? () => setCreateOpen(true)
            : undefined
        }
        onAddExisting={
          canAssignCandidate && vacancyAcceptsCandidates
            ? () => setAddExistingOpen(true)
            : undefined
        }
        addDisabledReason={
          !vacancyAcceptsCandidates && (canCreateCandidate || canAssignCandidate)
            ? 'Not available for this vacancy status'
            : undefined
        }
      />

      <BoardBody
        isLoading={isLoading}
        hasError={!!error}
        stages={displayStages}
        scrollRef={scrollRef}
        onScroll={handleScroll}
        dragDisabled={dragDisabled}
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        activeCard={activeCard}
        onOpenCard={handleOpenCard}
      />

      <CandidateProfileModal
        vacancy={vacancy}
        card={modalContext?.card ?? null}
        stage={modalContext?.stage ?? null}
        stages={baseStages ?? []}
        onMoveStage={dragDisabled ? undefined : handleMoveStageFromModal}
        isMoving={reorderMutation.isPending}
        open={modalContext !== null}
        onOpenChange={(next) => {
          if (!next) setOpenCardId(null);
        }}
        hasPrev={modalContext?.prevId != null}
        hasNext={modalContext?.nextId != null}
        onPrev={() => {
          if (modalContext?.prevId) setOpenCardId(modalContext.prevId);
        }}
        onNext={() => {
          if (modalContext?.nextId) setOpenCardId(modalContext.nextId);
        }}
        position={
          modalContext
            ? { index: modalContext.index, total: modalContext.total }
            : null
        }
      />

      <RejectReasonDialog
        open={pendingRejectMove !== null}
        card={pendingRejectMove?.card ?? null}
        onOpenChange={(open) => {
          if (!open && !reorderMutation.isPending) {
            setPendingRejectMove(null);
            setLocalStages(null);
          }
        }}
        onConfirm={(reasonId, comment) => {
          if (!pendingRejectMove) return;
          const { items, errorMsg, card } = pendingRejectMove;
          const itemsWithReason = items.map((it) =>
            it.id === card.candidateVacancyId
              ? { ...it, rejectionReasonId: reasonId, rejectionComment: comment || undefined }
              : it,
          );
          setPendingRejectMove(null);
          commitReorder(itemsWithReason, errorMsg);
        }}
        isPending={reorderMutation.isPending}
      />

      <MoveToOfferConfirmDialog
        open={pendingOfferMove !== null}
        onOpenChange={(open) => {
          if (!open && !reorderMutation.isPending) {
            setPendingOfferMove(null);
            setLocalStages(null);
          }
        }}
        onConfirm={() => {
          if (!pendingOfferMove) return;
          const { items, errorMsg } = pendingOfferMove;
          setPendingOfferMove(null);
          commitReorder(items, errorMsg);
        }}
        isPending={reorderMutation.isPending}
      />

      <HireCandidateDialog
        open={pendingHireMove !== null}
        lockedVacancyId={vacancy.id}
        lockedVacancyTitle={vacancy.title}
        candidate={pendingHireMove?.card.candidate ?? null}
        onOpenChange={(open) => {
          if (!open) setPendingHireMove(null);
        }}
        onHired={() => {
          setPendingHireMove(null);
          setLocalStages(null);
        }}
        onCancelled={() => {
          setLocalStages(null);
        }}
      />

      <VacancySettingsDialog
        vacancy={vacancy}
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
      />

      <CreateCandidateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSuccess={() => {
          setCreateOpen(false);
          queryClient.invalidateQueries({
            queryKey: recruitmentKeys.vacancyDetail(vacancy.id),
          });
        }}
        initialVacancyId={vacancy.id}
        initialVacancyTitle={vacancy.title}
      />

      <AddExistingCandidatesDialog
        vacancy={vacancy}
        open={addExistingOpen}
        onOpenChange={setAddExistingOpen}
      />
    </div>
  );
}

interface BoardBodyProps {
  isLoading: boolean;
  hasError: boolean;
  stages: PipelineStageBucket[] | null;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  dragDisabled: boolean;
  sensors: ReturnType<typeof useSensors>;
  onDragStart: (e: DragStartEvent) => void;
  onDragOver: (e: DragOverEvent) => void;
  onDragEnd: (e: DragEndEvent) => void;
  activeCard: PipelineCardData | null;
  onOpenCard: (card: PipelineCardData) => void;
}

function BoardBody({
  isLoading,
  hasError,
  stages,
  scrollRef,
  onScroll,
  dragDisabled,
  sensors,
  onDragStart,
  onDragOver,
  onDragEnd,
  activeCard,
  onOpenCard,
}: BoardBodyProps) {
  if (isLoading) {
    return (
      <div className="flex min-h-0 flex-1 gap-4 overflow-x-auto px-6 py-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-full w-72 shrink-0 animate-pulse rounded-xl border border-border/60 bg-muted/30"
          />
        ))}
      </div>
    );
  }

  if (hasError || !stages) {
    return (
      <div className="mx-6 my-4 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
        Failed to load pipeline.
      </div>
    );
  }

  if (stages.length === 0) {
    return (
      <div className="mx-6 my-4 rounded-md border border-dashed border-border px-6 py-12 text-center text-sm text-muted-foreground">
        No pipeline stages configured. Open the{' '}
        <span className="font-medium text-foreground">Pipeline</span> tab to add stages.
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex min-h-0 flex-1 gap-4 overflow-x-auto px-6 py-4"
      >
        {stages.map((stage) => {
          const cardIds = stage.cards.map((c) => c.candidateVacancyId);
          return (
            <PipelineColumn key={stage.id} stage={stage} count={stage.cards.length}>
              <SortableContext items={cardIds} strategy={verticalListSortingStrategy}>
                {stage.cards.map((card) => (
                  <PipelineCard
                    key={card.candidateVacancyId}
                    card={card}
                    onOpenCard={onOpenCard}
                    dragDisabled={dragDisabled}
                  />
                ))}
              </SortableContext>
            </PipelineColumn>
          );
        })}
      </div>

      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeCard ? (
            <PipelineCard
              card={activeCard}
              onOpenCard={() => undefined}
              isOverlay
            />
          ) : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
}
